import { useEffect, useRef, useState } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useOrganization, useUser } from '@clerk/react';
import { toast } from 'sonner';
import { GoogleDocsIcon } from '@/components/ui/icons/google-product-icons';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { followingUserText } from './question-card.util';
import { useGooglePicker } from '@/hooks/use-google-picker.hook';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatContext,
  selectAiChatConversationId,
  selectAiChatSessions,
  sendAiChatMessage,
} from '@/store/features/ui.feature';

const DOCS_LOGO = (
  <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
    <GoogleDocsIcon className='size-[12px]' />
  </div>
);

/** Grants the linked Google Doc for a Clu read of its text. */
export function GoogleDocReadCard({
  result,
  toolCallId,
}: {
  result: Record<string, unknown>;
  toolCallId: string;
}) {
  const { organization, isLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const context = useAppSelector(selectAiChatContext);
  const conversation = useAppSelector(selectAiChatConversationId);
  const sessions = useAppSelector(selectAiChatSessions);
  // Two plain values, not one object: useAuiState is useSyncExternalStore,
  // and a new object on every read never settles ("Maximum update depth").
  const state = {
    message: useAuiState((aui) => aui.message.id),
    available: useAuiState((aui) => {
      const tools = aui.message.content.filter(
        (part) => part.type === 'tool-call'
      );
      return (
        followingUserText(aui.thread.messages, aui.message.id) === null &&
        aui.thread.messages.at(-1)?.id === aui.message.id &&
        tools.at(-1)?.toolCallId === toolCallId
      );
    }),
  };
  if (
    !isLoaded ||
    !userLoaded ||
    !organization ||
    !user ||
    result.organization_id !== organization.id ||
    result.google_user_id !== user.id
  )
    return null;
  const scope = JSON.stringify([
    organization.id,
    user.id,
    context,
    conversation,
    sessions?.activeKey,
    state.message,
    state.available,
    toolCallId,
    result.file_id,
  ]);
  return <ReadCard key={scope} result={result} enabled={state.available} />;
}

function ReadCard({
  result,
  enabled,
}: {
  result: Record<string, unknown>;
  enabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const { canPick, pickDocs } = useGooglePicker('google_docs');
  const live = useRef(true);
  const busyRef = useRef(false);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const file =
    typeof result.file_id === 'string' &&
    /^[A-Za-z0-9_-]{10,200}$/.test(result.file_id)
      ? result.file_id
      : null;
  /** False unlocks the button: nothing was sent to Clu. */
  const read = async () => {
    if (!enabled || !canPick || !file || sent || busyRef.current) return false;
    busyRef.current = true;
    try {
      const picked = (await pickDocs([file])).find((doc) => doc.id === file);
      if (!live.current || !picked) return false;
      setSent(true);
      dispatch(
        sendAiChatMessage(
          `Read the Google Doc ${picked.name}: https://docs.google.com/document/d/${file}/edit`
        )
      );
      return true;
    } catch {
      if (live.current)
        toast.error("Couldn't open the doc with Google. Try again.");
      return false;
    } finally {
      busyRef.current = false;
    }
  };
  if (sent || !enabled)
    return (
      <AgentCard
        leading={DOCS_LOGO}
        title='Read Google Doc'
        trailing={
          sent ? (
            <CardStatus tone='success'>Shared with Clu</CardStatus>
          ) : (
            <CardStatus>Passed over</CardStatus>
          )
        }
      />
    );
  const fact = canPick ? 'Opens with Google' : 'Google Docs not connected';
  return (
    <AgentCard leading={DOCS_LOGO} title='Read Google Doc'>
      {canPick && file ? (
        <CardFooter
          control={<CardMeta className='min-w-0'>{fact}</CardMeta>}
          primary={{
            label: 'Read doc',
            workingLabel: 'Opening',
            onClick: read,
          }}
        />
      ) : (
        <CardMeta>{fact}</CardMeta>
      )}
    </AgentCard>
  );
}
